import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import Button from '../../components/common/Button';
import Loader from '../../components/common/Loader';
import './Orders.css';

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [expandedOrder, setExpandedOrder] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await api.get('/orders');
      setOrders(response.data.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
      alert('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  const confirmPayment = async (orderId) => {
    if (!window.confirm('Confirm this payment?')) return;
    
    try {
      await api.put(`/orders/${orderId}/confirm-payment`);
      alert('✅ Payment confirmed!');
      fetchOrders();
    } catch (error) {
      console.error('Error confirming payment:', error);
      alert('Failed to confirm payment');
    }
  };

  const toggleOrderDetails = (orderId) => {
    setExpandedOrder(expandedOrder === orderId ? null : orderId);
  };

  if (loading) return <Loader fullscreen />;

  return (
    <div className="orders-page">
      <div className="page-header">
        <h1 className="page-title">Orders</h1>
      </div>

      <div className="orders-container">
        {orders.map((order) => (
          <div key={order.id} className="order-card-admin">
            {/* Order Header - Always Visible */}
            <div className="order-card-header" onClick={() => toggleOrderDetails(order.id)}>
              <div className="order-summary-row">
                <div className="order-info-group">
                  <span className="order-id">Order #{order.id}</span>
                  <span className="order-date">{new Date(order.created_at).toLocaleString()}</span>
                </div>
                
                <div className="order-info-group">
                  <span className="customer-name">{order.customer_name}</span>
                  <span className="order-total">{parseFloat(order.total).toFixed(2)} ₾</span>
                </div>

                <div className="order-info-group">
                  <span className={`status-badge status-${order.payment_status}`}>
                    {order.payment_status.replace('_', ' ')}
                  </span>
                  {order.payment_method === 'bog_ipay' ? '💳 BOG iPay' : '🏦 Bank Transfer'}
                </div>

                <div className="order-actions-quick">
                  {order.payment_status === 'pending_verification' && (
                    <Button
                      variant="success"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        confirmPayment(order.id);
                      }}
                    >
                      ✓ Confirm
                    </Button>
                  )}
                  <span className="expand-icon">{expandedOrder === order.id ? '▼' : '▶'}</span>
                </div>
              </div>
            </div>

            {/* Expanded Details */}
            {expandedOrder === order.id && (
              <div className="order-details-expanded">
                {/* Order Items Section */}
                {order.items && order.items.length > 0 && (
                  <div className="detail-section full-width">
                    <h3>📦 Order Items ({order.items.length} items)</h3>
                    <div className="order-items-list">
                      {order.items.map((item, index) => (
                        <div key={index} className="order-item-card">
                          <div className="order-item-image">
                            {item.image_url ? (
                              <img 
                                src={`https://kid-o.ge${item.image_url}`} 
                                alt={item.product_name}
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23f0f0f0" width="100" height="100"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999" font-size="14"%3ENo Image%3C/text%3E%3C/svg%3E';
                                }}
                              />
                            ) : (
                              <div className="no-image-placeholder">📦</div>
                            )}
                          </div>
                          <div className="order-item-details">
                            <div className="order-item-name">{item.product_name}</div>
                            <div className="order-item-info">
                              <span className="order-item-quantity">Qty: {item.quantity}</span>
                              <span className="order-item-price">{parseFloat(item.price).toFixed(2)} ₾</span>
                            </div>
                            <div className="order-item-total">
                              Total: {(parseFloat(item.price) * parseInt(item.quantity)).toFixed(2)} ₾
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="details-grid">
                  {/* Customer Information */}
                  <div className="detail-section">
                    <h3>👤 Customer Information</h3>
                    <div className="detail-row">
                      <span className="detail-label">Name:</span>
                      <span className="detail-value">{order.customer_name}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Email:</span>
                      <span className="detail-value">{order.email}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Phone:</span>
                      <span className="detail-value">{order.phone}</span>
                    </div>
                  </div>

                  {/* Shipping Address */}
                  <div className="detail-section">
                    <h3>📍 Shipping Address</h3>
                    <div className="detail-row">
                      <span className="detail-label">Address:</span>
                      <span className="detail-value">{order.shipping_address}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Postal Code:</span>
                      <span className="detail-value">{order.postal_code || 'N/A'}</span>
                    </div>
                  </div>

                  {/* Order Summary */}
                  <div className="detail-section">
                    <h3>💰 Order Summary</h3>
                    <div className="detail-row">
                      <span className="detail-label">Subtotal:</span>
                      <span className="detail-value">{parseFloat(order.subtotal).toFixed(2)} ₾</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Shipping:</span>
                      <span className="detail-value">{parseFloat(order.shipping_cost).toFixed(2)} ₾</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Tax:</span>
                      <span className="detail-value">{parseFloat(order.tax).toFixed(2)} ₾</span>
                    </div>
                    <div className="detail-row total-row">
                      <span className="detail-label">Total:</span>
                      <span className="detail-value">{parseFloat(order.total).toFixed(2)} ₾</span>
                    </div>
                  </div>

                  {/* Payment & Status */}
                  <div className="detail-section">
                    <h3>💳 Payment & Status</h3>
                    <div className="detail-row">
                      <span className="detail-label">Payment Method:</span>
                      <span className="detail-value">
                        {order.payment_method === 'bog_ipay' ? '💳 BOG iPay' : '🏦 Bank Transfer'}
                      </span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Payment Status:</span>
                      <span className={`status-badge status-${order.payment_status}`}>
                        {order.payment_status.replace('_', ' ')}
                      </span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Order Status:</span>
                      <span className="detail-value">{order.status}</span>
                    </div>
                    {order.receipt_url && (
                      <div className="detail-row">
                        <span className="detail-label">Receipt:</span>
                        <button
                          className="view-receipt-btn"
                          onClick={() => setSelectedReceipt(`https://kid-o.ge${order.receipt_url}`)}
                        >
                          📸 View Receipt
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Notes */}
                  {order.notes && (
                    <div className="detail-section full-width">
                      <h3>📝 Order Notes</h3>
                      <p className="order-notes">{order.notes}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Receipt Modal */}
      {selectedReceipt && (
        <div className="receipt-modal" onClick={() => setSelectedReceipt(null)}>
          <div className="receipt-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedReceipt(null)}>×</button>
            <img src={selectedReceipt} alt="Receipt" />
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;